import {
  AfterContentInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Injectable,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { calendarLang } from '../../../constants/calendar-lang.constants';
import DATE_CONSTANTS from '../../../constants/date.constants';
import { AvailabilityDatesService } from '../../../core/services/availability-dates/availability-dates.service';
import { IAvailabilityDates } from '../../../core/interfaces/availability-dates';
import { Subscription } from 'rxjs';
import {
  NgbDate,
  NgbDatepicker,
  NgbDatepickerI18n,
  NgbDatepickerNavigateEvent,
  NgbDateStruct
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import endOfMonth from 'date-fns/endOfMonth';
import setHours from 'date-fns/setHours';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import setDate from 'date-fns/setDate';
import startOfMonth from 'date-fns/startOfMonth';
import getMonth from 'date-fns/getMonth';
import compareAsc from 'date-fns/compareAsc';
import getYear from 'date-fns/getYear';
import isAfter from 'date-fns/isAfter';
import isEqual from 'date-fns/isEqual';
import { CookieAppService } from '@shared/common/services/cookie-app/cookie-app.service';
import { IDate } from '../../../core/interfaces/datepicker-date';
import { TranslationSlab } from '../../../core/classes/translation-slab/translation-slab';
import { LocationService } from '../../../core/services/location/location.service';
import { BREAKPOINTS_WIDTH } from '@shared/common/services/viewport/constants/breakpoint.constant';

@Injectable()
export class I18n extends TranslationSlab {
  visibleDatePicker = true;
  translateSubscription: Subscription;

  constructor(
    public translateService: TranslateService,
    public cookieAppService: CookieAppService,
    public locationService: LocationService,
    @Inject(PLATFORM_ID) public platformId: Object
  ) {
    super(translateService, cookieAppService, locationService, platformId);

    this.translateSubscription = this.changingLangEvent().subscribe(() => {
      this.visibleDatePicker = false;
      setTimeout(() => {
        this.visibleDatePicker = true;
      });
    });
  }
}

@Injectable()
export class CustomDatepickerI18n extends NgbDatepickerI18n {
  constructor(public _i18n: I18n) {
    super();
  }

  getMonthShortName(month: number): string {
    return calendarLang[this._i18n.currentLang].months[month - 1];
  }
  getMonthFullName(month: number): string {
    return this.getMonthShortName(month);
  }
  getDayAriaLabel(date: NgbDateStruct): string {
    return `${date.day} - ${date.month} - ${date.year}`;
  }

  getWeekdayLabel(weekday: number, width?: 'long' | 'short' | 'narrow'): string {
    return calendarLang[this._i18n.currentLang].weekdays[weekday - 1];
  }
}

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  providers: [I18n, { provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n }],
  standalone: false
})
export class CalendarComponent implements OnInit, AfterContentInit, OnDestroy {
  selectedDates: Date[] = [];
  selectHasStarted: boolean;
  visibleBookedDates = false;
  currentWindowWidth: number;
  availabilityDates: IAvailabilityDates;
  daysInfoObj: { [key: string]: { date: string; isBookable: boolean; offer: { price: number } }[] } = {};
  showInaccessibleError = false;
  adults = 1;
  childrenAges = '';
  selectedCityIdSubscription: Subscription;
  guestsDataSubscription: Subscription;
  availabilityDatesServiceSubscription: Subscription;
  activatedRouteParamsSubscription: Subscription;
  displayMonths: number;
  isVisible: boolean;
  hoveredDate: NgbDate;
  fromDate: IDate;
  toDate: IDate;
  currentDate: Date;
  initialWindowWidth: number;
  monthName: string;
  year: string;
  currentDisplayedMonths: string[];
  rangeInterval: Date[] = [];
  calendarStartDate: IDate;
  showSelectDatesMessage: boolean;
  extensionToDate: string;
  @ViewChild('scrollContainer') scrollContainer: ElementRef;
  @ViewChild('dp') dp: NgbDatepicker;
  @Input() set isHidden(v: boolean) {
    if (v) {
      this.showInaccessibleError = false;
    } else {
      this.showSelectDatesMessage =
        this.calendarType === 'listing' && (this.dateRange || !this.dateRange) && !this.dateRange[0];
    }
    this.isVisible = !v;

    if (this.fromDate) {
      if (this.dp) {
        this.dp.navigateTo(this.fromDate);
      }
    }

    if (this.toDate) {
      this.checkDatesOnUnavailability(this.toDate);
    }

    if (this.dateRange && this.dateRange.length === 2 && this.dateRange[0]) {
      let isDateString = typeof this.dateRange[0] === 'string' || this.dateRange[0] instanceof String;

      this.fromDate.year = isDateString
        ? new Date(this.dateRange[0] + 'T00:00').getFullYear()
        : (this.dateRange[0] as Date).getFullYear();
      this.fromDate.month = isDateString
        ? new Date(this.dateRange[0] + 'T00:00').getMonth() + 1
        : (this.dateRange[0] as Date).getMonth() + 1;
      this.fromDate.day = isDateString
        ? new Date(this.dateRange[0] + 'T00:00').getDate()
        : (this.dateRange[0] as Date).getDate();

      this.toDate.year = isDateString
        ? new Date(this.dateRange[1] + 'T00:00').getFullYear()
        : (this.dateRange[1] as Date).getFullYear();
      this.toDate.month = isDateString
        ? new Date(this.dateRange[1] + 'T00:00').getMonth() + 1
        : (this.dateRange[1] as Date).getMonth() + 1;
      this.toDate.day = isDateString
        ? new Date(this.dateRange[1] + 'T00:00').getDate()
        : (this.dateRange[1] as Date).getDate();
    }
  }
  @Input() calendarType: 'pdp' | 'listing' | 'other';
  @Input() dateRange: string[] | Date[];
  @Input() cityId: number;
  @Input() propertyId: number;
  @Input() unitGroupId: number;
  @Input() isEmptyDates: boolean;
  @Input() minDate: IDate;
  @Input() maxDate: IDate;
  @Input() isExtension: boolean;
  @Output() bookedDates: EventEmitter<Date[]>;
  @Output() hideCalendar: EventEmitter<boolean>;

  get isElements(): boolean {
    return !!(window as any).__isAngularElement__;
  }

  constructor(
    private readonly availabilityDatesService: AvailabilityDatesService,
    public elemRef: ElementRef,
    public I18n: I18n
  ) {
    this.selectHasStarted = false;
    this.bookedDates = new EventEmitter();
    this.hideCalendar = new EventEmitter();
  }

  ngOnInit() {
    this.setMinMaxDates();
    this.initialWindowWidth = window.innerWidth;
    this.currentWindowWidth = window.innerWidth;
    this.buildCalendarViewForResolutions(this.currentWindowWidth);
    this.selectedCityIdSubscription = this.availabilityDatesService.selectedCityId.subscribe((cityId) => {
      if (cityId) {
        this.cityId = cityId;
        this.daysInfoObj = {};
        if (this.currentDisplayedMonths && this.currentDisplayedMonths.length) {
          this.getAvailabilityStatusForDates(this.currentDisplayedMonths[0], this.currentDisplayedMonths[1]);
        }
      }
    });
    this.guestsDataSubscription = this.availabilityDatesService.guestsData.subscribe((guestsData) => {
      if (guestsData && guestsData.adults && this.cityId) {
        this.adults = guestsData.adults;
        this.childrenAges = guestsData.childrenAges;
        this.daysInfoObj = {};
        if (this.currentDisplayedMonths && this.currentDisplayedMonths.length) {
          this.getAvailabilityStatusForDates(this.currentDisplayedMonths[0], this.currentDisplayedMonths[1]);
        }
      }
    });
  }

  ngAfterContentInit() {
    this.setStartDateRange();
  }

  ngOnDestroy() {
    this.availabilityDatesService.selectedCityId.next(null);

    if (this.selectedCityIdSubscription) {
      this.selectedCityIdSubscription.unsubscribe();
    }

    if (this.guestsDataSubscription) {
      this.guestsDataSubscription.unsubscribe();
    }

    if (this.availabilityDatesServiceSubscription) {
      this.availabilityDatesServiceSubscription.unsubscribe();
    }

    if (this.I18n.translateSubscription) {
      this.I18n.translateSubscription.unsubscribe();
    }

    if (this.activatedRouteParamsSubscription) {
      this.activatedRouteParamsSubscription.unsubscribe();
    }
  }

  /**
   * @description
   * Change calendar view for different resolutions
   *
   */
  buildCalendarViewForResolutions(winWidth: number): void {
    this.currentWindowWidth = winWidth;

    if (this.currentWindowWidth >= BREAKPOINTS_WIDTH.MOBILE) {
      this.visibleBookedDates = false;

      this.displayMonths = 2;
    } else {
      this.displayMonths = 1;
    }

    if (this.isExtension) {
      this.visibleBookedDates = false;
      this.displayMonths = 1;
    }
  }

  /**
   * @description
   * Confirm selected dates
   *
   */
  confirmBookedDates(): void {
    this.bookedDates.emit(this.selectedDates);
    this.closeCalendar();
    this.visibleBookedDates = false;
  }

  /**
   * @description
   * Close the calendar component
   *
   */
  closeCalendar(): void {
    this.hideCalendar.emit(false);

    if (this.isElements) {
      document.body.style.overflow = '';
      const header = document.querySelector('[data-elementor-type=header]') as HTMLElement;
      if (header) {
        header.style.display = 'block';
      }
    }
  }

  /**
   * @description
   * Get array with available status for time period dates
   */
  getAvailabilityStatusForDates(startDate: string, endDate: string): void {
    this.availabilityDatesServiceSubscription = this.availabilityDatesService
      .getAvailabilityDates(
        this.cityId,
        startDate.slice(0, 7),
        endDate.slice(0, 7),
        this.adults,
        this.childrenAges,
        this.propertyId,
        this.unitGroupId
      )
      .subscribe((dates) => {
        if (dates.success) {
          this.availabilityDates = dates.payload;
          this.daysInfoObj = { ...this.daysInfoObj, ...this.availabilityDates };
          this.checkDatesOnUnavailability(this.toDate);

          if (this.isExtension) {
            this.checkExtensionUnavailable(this.currentDisplayedMonths[1]);
          }
        }
      });
  }

  /**
   * @description
   * Event while selecting dates range
   *
   */
  onDateSelection(date: NgbDate) {
    this.selectedDates = [];

    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
      this.selectedDates = [new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day)];
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.checkDatesOnUnavailability(date);
      this.toDate = date;
      this.selectedDates = [
        new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day),
        new Date(this.toDate.year, this.toDate.month - 1, this.toDate.day)
      ];
    } else {
      this.toDate = null;
      this.fromDate = date;
      this.selectedDates = [new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day)];
    }
    this.updateVisibilityBookedDates();

    if (this.fromDate && this.toDate && !this.checkDatesOnUnavailability(date)) {
      if (this.currentWindowWidth >= BREAKPOINTS_WIDTH.MOBILE) {
        this.bookedDates.emit(this.selectedDates);
      }
    }
  }

  /**
   * @description
   * navigation calendar months
   */
  navigate(event: NgbDatepickerNavigateEvent) {
    let startDate;
    let endDate;
    let direction: string;

    if (
      !event.current ||
      (event.current !== null &&
        compareAsc(
          setHours(new Date(event.next.year, event.next.month - 1), 0),
          setHours(new Date(event.current.year, event.current.month - 1), 0)
        ) > 0)
    ) {
      if (
        getYear(new Date(event.next.year, event.next.month - 1)) === getYear(new Date()) &&
        getMonth(new Date(event.next.year, event.next.month - 1)) === getMonth(new Date())
      ) {
        startDate = format(new Date(), DATE_CONSTANTS.renderApiDateFormat);
      } else {
        startDate = format(
          startOfMonth(new Date(event.next.year, event.next.month - 1)),
          DATE_CONSTANTS.renderApiDateFormat
        );
      }
      endDate = format(
        addDays(endOfMonth(new Date(event.next.year, event.next.month)), 1),
        DATE_CONSTANTS.renderApiDateFormat
      );
      direction = 'forth';
    } else if (
      event.current !== null &&
      compareAsc(
        setHours(new Date(event.current.year, event.current.month - 1), 0),
        setHours(new Date(event.next.year, event.next.month - 1), 0)
      ) > 0
    ) {
      if (
        getYear(new Date(event.next.year, event.next.month - 1)) === getYear(new Date()) &&
        getMonth(new Date(event.next.year, event.next.month - 1)) === getMonth(new Date())
      ) {
        startDate = format(new Date(), DATE_CONSTANTS.renderApiDateFormat);
      } else {
        startDate = format(
          startOfMonth(new Date(event.next.year, event.next.month - 1)),
          DATE_CONSTANTS.renderApiDateFormat
        );
      }
      endDate = format(
        addDays(endOfMonth(new Date(event.next.year, event.next.month)), 1),
        DATE_CONSTANTS.renderApiDateFormat
      );
      direction = 'back';
    }

    this.currentDisplayedMonths = [startDate, endDate];

    if (
      this.isVisible &&
      this.cityId &&
      !this.checkAvailabilityDatesArrayForExisting(this.currentDisplayedMonths, direction)
    ) {
      this.getAvailabilityStatusForDates(this.currentDisplayedMonths[0], this.currentDisplayedMonths[1]);
    }

    setTimeout(() => {
      this.createMonthTitle(event.next.year, event.next.month);
      this.calendarStartDate = this.fromDate;
    });
  }

  /**
   * @description
   * check object with availability dates(daysInfoObj) if it already has calendar visible months
   */
  checkAvailabilityDatesArrayForExisting(dates: string[], direction: string) {
    let key;

    if (direction === 'forth') {
      key = dates[1].split('-')[0] + '-' + dates[1].split('-')[1];
    } else {
      key = dates[0].split('-')[0] + '-' + dates[0].split('-')[1];
    }

    return this.daysInfoObj.hasOwnProperty(key);
  }

  /**
   * @description
   * create month and year title (on mobile view only)
   */
  createMonthTitle(year: number, month: number) {
    this.monthName = format(setDate(new Date(year, month - 1), 1), 'MMMM');
    this.year = format(setDate(new Date(year, month - 1), 1), 'yyyy');
  }

  /**
   * @description
   * check if the desired interval contains inaccessible dates
   */
  checkDatesOnUnavailability(date: IDate) {
    let isUnavailableExist = false;

    if (date) {
      this.rangeInterval = eachDayOfInterval({
        start: new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day),
        end: new Date(date.year, date.month - 1, date.day)
      });

      this.rangeInterval.map((rangeItem) => {
        let keyProperty = this.daysInfoObj.hasOwnProperty(format(rangeItem, 'yyyy-MM'));
        if (keyProperty) {
          let dateItems = this.daysInfoObj[format(rangeItem, 'yyyy-MM')];

          for (let i = 0; i <= dateItems.length - 1; i++) {
            if (compareAsc(new Date(dateItems[i].date + 'T00:00'), setHours(rangeItem, 0)) === 0) {
              // Listing page and PDP have different availabilities endpoints
              const isBookable = dateItems[i].isBookable;
              if (!isBookable) {
                if (dateItems[i].date !== format(new Date(date.year, date.month - 1, date.day), 'yyyy-MM-dd')) {
                  isUnavailableExist = true;
                  return;
                }
              }
            }
          }
        }
      });
    }

    this.showInaccessibleError = isUnavailableExist;

    return isUnavailableExist;
  }

  setStartDateRange() {
    if (this.dateRange && this.dateRange.length) {
      if (this.dateRange[0]) {
        this.fromDate = {
          year: Number((this.dateRange[0] as String).split('-')[0]),
          month: Number((this.dateRange[0] as String).split('-')[1]),
          day: Number((this.dateRange[0] as String).split('-')[2])
        };
      }

      if (this.dateRange[1]) {
        this.toDate = {
          year: Number((this.dateRange[1] as String).split('-')[0]),
          month: Number((this.dateRange[1] as String).split('-')[1]),
          day: Number((this.dateRange[1] as String).split('-')[2])
        };
      }
    }
  }

  /**
   * @description
   * set minimal and maximal period of calendar
   */
  setMinMaxDates() {
    this.currentDate = new Date();

    if (!this.minDate) {
      this.minDate = {
        year: this.currentDate.getFullYear(),
        month: this.currentDate.getMonth() + 1,
        day: this.currentDate.getDate()
      };
    }

    if (!this.maxDate) {
      this.maxDate = {
        year: this.currentDate.getFullYear() + 1,
        month: this.currentDate.getMonth() + 1,
        day: this.currentDate.getDate()
      };
    }
  }

  /**
   * @description
   * Function for show/hide confirm footer on mobile view
   *
   */
  updateVisibilityBookedDates(): void {
    if (this.selectedDates.length && this.currentWindowWidth < BREAKPOINTS_WIDTH.MOBILE) {
      this.visibleBookedDates = true;
    }
  }

  checkExtensionUnavailable(endDate: string) {
    if (!endDate || !this.dateRange?.[0]) {
      return;
    }

    let unavailableDateString: string;
    const reservationEndDate = new Date(this.dateRange[0] || endDate);

    for (const daysInfoDate in this.daysInfoObj) {
      if (!Object.prototype.hasOwnProperty.call(this.daysInfoObj, daysInfoDate) || unavailableDateString) {
        continue;
      }

      const daysInfo = this.daysInfoObj[daysInfoDate];
      unavailableDateString = daysInfo?.find(
        // look for a date that's the same or after the end date of the booking
        // if that date is not bookable then that's the date extension is unavailable from
        ({ date, isBookable }) => !isBookable && this.isAfterOrEqual(new Date(date), reservationEndDate)
      )?.date;
    }

    if (!unavailableDateString) {
      return;
    }

    const newMaxDate = addDays(new Date(unavailableDateString), 1);
    this.extensionToDate = unavailableDateString;
    this.maxDate = { year: newMaxDate.getFullYear(), month: newMaxDate.getMonth() + 1, day: newMaxDate.getDate() };
  }

  isAfterOrEqual(date: Date, minDate: Date) {
    return isAfter(date, minDate) || isEqual(date, minDate);
  }

  /**
   * @description
   * Listen resize event to change calendar view
   *
   */
  @HostListener('window:resize', ['$event'])
  onResize(event): void {
    const newWindowWidth = event.target.innerWidth;
    if (this.initialWindowWidth !== newWindowWidth) {
      this.buildCalendarViewForResolutions(newWindowWidth);
      // Fix for BW-411: when the cities overlay is open and the user scrolls down, the browser hides the address bar. Then a resize event is triggered
      if (this.isVisible) {
        this.closeCalendar();
      }
      this.initialWindowWidth = newWindowWidth;
    }
  }
}
